import { useMemo } from "react";
import { MetricCardsContainer } from "@/components/metrics/metric-cards-container";
import type { MetricCardConfig } from "@/components/metrics/types";
import { useMetricCardFilters } from "@/hooks/use-metric-card-filters";
import { useSalespersonFilterState } from "@/hooks/use-salesperson-filter-state";
import type { SalespersonMetrics } from "@/types/salesperson-types";
import {
  DEFAULT_METRIC_ORDER,
  HIDDEN_METRICS,
  METRIC_CONFIG,
  type SalespersonMetric,
  renderMetric as salespersonRenderMetric,
  WIP_METRICS,
} from "./salesperson-metrics-config";

interface SalespersonMetricCardsProps {
  metrics: SalespersonMetrics | undefined;
  onMetricSelect: (metric: SalespersonMetric) => void;
  loadingStates: Record<
    SalespersonMetric,
    { isLoading: boolean; isError: boolean }
  >;
  leftControls?: React.ReactNode;
  showBody?: boolean;
  emptyBody?: React.ReactNode;
}

export function SalespersonMetricCards({
  metrics,
  onMetricSelect,
  loadingStates,
  leftControls,
  showBody,
  emptyBody,
}: SalespersonMetricCardsProps) {
  const filterState = useSalespersonFilterState("salespeople::explorer");
  const {
    displayFilters,
    filterColumns,
    getTotalFilterCount,
    handleClearFilter,
    clearAllFilters,
  } = useMetricCardFilters(filterState);

  // Convert METRIC_CONFIG to the format expected by MetricCardsContainer
  const convertedMetricConfig = useMemo(() => {
    const config: Record<string, MetricCardConfig> = {};

    Object.entries(METRIC_CONFIG).forEach(([key, value]) => {
      config[key] = {
        title: value.title,
        tooltip: value.tooltip,
        category: value.category,
        render: value.render
          ? (val: number | null, options: Record<string, unknown>) => {
              // Convert generic options to the specific format expected by salesperson metrics
              return value.render!(val, {
                metricKey: options.metricKey as SalespersonMetric,
                metrics: options.metrics as SalespersonMetrics | undefined,
                isBenchmark: options.isBenchmark as boolean | undefined,
              });
            }
          : undefined,
      };
    });

    return config;
  }, []);

  // Wrapper for renderMetric function
  const renderMetric = (
    value: number | null,
    options: Record<string, unknown>,
  ) => {
    return salespersonRenderMetric(value, {
      metricKey: options.metricKey as SalespersonMetric,
      metrics: options.metrics as SalespersonMetrics | undefined,
      isBenchmark: options.isBenchmark as boolean | undefined,
    });
  };

  return (
    <MetricCardsContainer
      metrics={metrics}
      onMetricSelect={(metric) => onMetricSelect(metric as SalespersonMetric)}
      loadingStates={
        loadingStates as Record<
          string,
          { isLoading: boolean; isError: boolean }
        >
      }
      metricConfig={convertedMetricConfig}
      defaultOrder={DEFAULT_METRIC_ORDER as string[]}
      hiddenMetrics={HIDDEN_METRICS as string[]}
      wipMetrics={WIP_METRICS as string[]}
      storageKey="salespeople::explorer::metrics::order"
      title="Top-level rep metrics"
      entityScope="salespeople::explorer"
      entity="salesperson"
      renderMetric={renderMetric}
      getTotalFilterCount={getTotalFilterCount}
      onResetMetricFilters={clearAllFilters}
      displayFilters={displayFilters}
      filterColumns={filterColumns}
      clearFilter={handleClearFilter}
      leftControls={leftControls}
      showBody={showBody}
      emptyBody={emptyBody}
    />
  );
}
